// Leaflet-free, tile-free GPS trace: normalise lat/lng into an SVG viewBox and
// draw the polyline plus discrepancy markers. Good enough for the scaffold and
// works entirely offline (no external map tiles).

import type { Discrepancy } from "../types.js";
import type { TracePoint } from "../timeline.js";

interface Props {
  trace: TracePoint[];
  discrepancies: Discrepancy[];
  // Optional "current position" dot for the play-through cursor.
  cursor?: { lat: number; lng: number } | null;
}

const W = 480;
const H = 320;
const PAD = 24;

interface Bounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

function bounds(points: Array<{ lat: number; lng: number }>): Bounds | null {
  if (points.length === 0) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  return { minLat, maxLat, minLng, maxLng };
}

function projector(b: Bounds) {
  const latSpan = b.maxLat - b.minLat || 1e-6;
  const lngSpan = b.maxLng - b.minLng || 1e-6;
  return (lat: number, lng: number): [number, number] => {
    const x = PAD + ((lng - b.minLng) / lngSpan) * (W - 2 * PAD);
    // Latitude increases upward, so invert the y axis.
    const y = PAD + (1 - (lat - b.minLat) / latSpan) * (H - 2 * PAD);
    return [x, y];
  };
}

const sevColor: Record<string, string> = {
  info: "var(--info)",
  warn: "var(--warn)",
  error: "var(--error)",
};

export function MapTrace({ trace, discrepancies, cursor }: Props) {
  const all = [
    ...trace,
    ...discrepancies.filter((d) => d.lat != null && d.lng != null).map((d) => ({ lat: d.lat as number, lng: d.lng as number })),
  ];
  const b = bounds(all);
  if (!b) {
    return (
      <svg className="map" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="no GPS trace">
        <text x={W / 2} y={H / 2} fill="var(--muted)" textAnchor="middle">
          no GPS points in this trip
        </text>
      </svg>
    );
  }
  const project = projector(b);
  const path = trace.map((p) => project(p.lat, p.lng).join(",")).join(" ");

  return (
    <svg className="map" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="GPS trace">
      <rect x={0} y={0} width={W} height={H} fill="none" />
      {trace.length > 1 && (
        <polyline points={path} fill="none" stroke="var(--accent)" strokeWidth={2} opacity={0.8} />
      )}
      {trace.map((p, i) => {
        const [x, y] = project(p.lat, p.lng);
        return <circle key={`t${i}`} cx={x} cy={y} r={2.5} fill="var(--accent)" />;
      })}
      {discrepancies
        .filter((d) => d.lat != null && d.lng != null)
        .map((d, i) => {
          const [x, y] = project(d.lat as number, d.lng as number);
          return (
            <g key={`d${i}`}>
              <circle cx={x} cy={y} r={6} fill="none" stroke={sevColor[d.severity]} strokeWidth={2} />
              <circle cx={x} cy={y} r={2} fill={sevColor[d.severity]} />
            </g>
          );
        })}
      {cursor && (() => {
        const [x, y] = project(cursor.lat, cursor.lng);
        return <circle cx={x} cy={y} r={5} fill="var(--ok)" stroke="#fff" strokeWidth={1.5} />;
      })()}
    </svg>
  );
}
